import { createAsyncThunk } from "@reduxjs/toolkit";
import { ICorporateServiceOption } from "../../interfaces/corporateEvent";
import { API_ENDPOINTS } from "../../constants/apiConstants";
import { iThunkApi } from "../../types/commonTypes";
import {
	iEventType,
	iFilterType,
	iRespondEventPayload,
	iEventBids,
	iEventDetailCMS,
	iFilePayload,
	iFileResponse,
	iEventActionResp,
	IWhatsAppGroupInfo,
} from "../../types/eventTypes";
import { API_METHOD_TYPE, apiManager, isApiSuccess } from "../apiManager";
import { iApiSuccess } from "../../types/commonTypes";
import { REACT_APP_CMS_URL } from "../../constants/envConstants";
import { REACT_APP_API_URL } from "../../constants/envConstants";
import { apiCommonManager } from "../apiCommonManager";
import { getAccessToken } from "../../helpers/authHelpers";
import { ICreateQuoteForm } from "../../types/quoteFormTypes";

export const getEventsThunk = createAsyncThunk<
	Array<iEventType>,
	{ type: number; isLoading?: boolean },
	iThunkApi
>("event/getEvents", async ({ type }, thunkAPI) => {
	const resp = await apiManager(API_ENDPOINTS.EVENT_LIST(type), null, API_METHOD_TYPE.GET, {});
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data.data;
});
export const createFilterThunk = createAsyncThunk<iApiSuccess, iFilterType, iThunkApi>(
	"event/createFilter",
	async (payload, thunkAPI) => {
		const resp = await apiManager(API_ENDPOINTS.CREATE_EVENT_FILTERS, payload, "POST", {});
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data;
	},
);
export const getEventHistoryThunk = createAsyncThunk<
	Array<iEventType>,
	{ type: number; isLoading: boolean },
	iThunkApi
>("event/getEventHistory", async ({ type }, thunkAPI) => {
	const resp = await apiManager(API_ENDPOINTS.EVENT_HISTORY(type), null, API_METHOD_TYPE.GET, {});
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data.data;
});
export const respondToEventFilterThunk = createAsyncThunk<iEventActionResp, iRespondEventPayload, iThunkApi>(
	"event/respondToEvent",
	async (payload, thunkAPI) => {
		const resp = await apiManager(API_ENDPOINTS.EVENT_RESPOND(payload.id), payload, "POST", {});
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data;
	},
);
export const getEventDetailThunk = createAsyncThunk<iEventType, { id: string }, iThunkApi>(
	"event/getEventDetail",
	async ({ id }, thunkAPI) => {
		const resp = await apiManager(API_ENDPOINTS.EVENT_DETAIL(id), null, API_METHOD_TYPE.GET, {});
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data;
	},
);

export const getEventBidThunk = createAsyncThunk<iEventBids, { id: string }, iThunkApi>(
	"event/getEventBid",
	async ({ id }, thunkAPI) => {
		const resp = await apiManager(API_ENDPOINTS.EVENT_BID(id), null, API_METHOD_TYPE.GET, {});
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data;
	},
);

export const saveBidThunk = createAsyncThunk<iApiSuccess, ICreateQuoteForm, iThunkApi>(
	"event/saveBid",
	async (payload, thunkAPI) => {
		const resp = await apiManager(API_ENDPOINTS.SAVE_EVENT_BID, payload, API_METHOD_TYPE.POST, {});
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data;
	},
);

export const getEventFilterThunk = createAsyncThunk<iFilterType, { gridId: string }, iThunkApi>(
	"event/getEventFilter",
	async ({ gridId }, thunkAPI) => {
		const resp = await apiManager(
			API_ENDPOINTS.GET_GRID_FILTERS + `/${gridId}`,
			null,
			API_METHOD_TYPE.GET,
			{},
		);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data;
	},
);
export const getEventValuesCMSThunk = createAsyncThunk<iEventDetailCMS, {}, iThunkApi>(
	"event/getEventcms",
	async (args, thunkAPI) => {
		const resp = await apiManager(
			`/api/event-form-data/additional-data/mappings?locale=en`,
			null,
			API_METHOD_TYPE.GET,
			{},
			REACT_APP_CMS_URL,
		);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data;
	},
);
export const getCorporateServiceOptionsThunk = createAsyncThunk<ICorporateServiceOption[], undefined, iThunkApi>(
	"event/getCorporateServiceOptions",
	async (args, thunkAPI) => {
		const resp = await apiManager(
			API_ENDPOINTS.GET_CORPORATE_INQUIRY_FORM_DATA,
			null,
			API_METHOD_TYPE.GET,
			{},
			REACT_APP_CMS_URL,
		);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data?.services?.options ?? [];
	},
);
export const fileUploadThunk = createAsyncThunk<iFileResponse, iFilePayload, iThunkApi>(
	"consultation/fileUpload",
	async ({ file, directory }, thunkAPI) => {
		const formData = new FormData();

		if (file) {
			formData.append("file", file!);
		}
		formData.append("directory", directory);

		const resp = await apiCommonManager.request(
			API_ENDPOINTS.UPLOAD_FILE,
			formData,
			"POST",
			getAccessToken(),
			REACT_APP_API_URL,
			true,
		);
		if (resp.data.status === false) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data;
	},
);

export const getEventRequestThunk = createAsyncThunk<any, null | boolean, iThunkApi>(
	"event/getEventRequest",
	async (_, thunkAPI) => {
		const resp = await apiManager(API_ENDPOINTS.EVENT_REQUESTS, null, API_METHOD_TYPE.GET, {});
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data.data;
	},
);

export const declineEventRequestThunk = createAsyncThunk<any, string, iThunkApi>(
	"event/declineEventRequestThunk",
	async (eventRequestId, thunkAPI) => {
		const resp = await apiManager(
			API_ENDPOINTS.EVENT_REQUEST_DECLINE(eventRequestId),
			null,
			API_METHOD_TYPE.PUT,
			{},
		);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data;
	},
);

export const acceptEventRequestThunk = createAsyncThunk<any, string, iThunkApi>(
	"event/acceptEventRequestThunk",
	async (eventRequestId, thunkAPI) => {
		const resp = await apiManager(
			API_ENDPOINTS.EVENT_REQUEST_ACCEPT(eventRequestId),
			null,
			API_METHOD_TYPE.PUT,
			{},
		);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data;
	},
);

export const expireEventRequestThunk = createAsyncThunk<any, string, iThunkApi>(
	"event/expireEventRequest",
	async (eventRequestId, thunkAPI) => {
		const resp = await apiManager(
			API_ENDPOINTS.EVENT_REQUEST_EXPIRE(eventRequestId),
			null,
			API_METHOD_TYPE.PATCH,
			{},
		);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data;
	},
);

export const getVendorEventGroupRecord = createAsyncThunk<
	IWhatsAppGroupInfo,
	{ vendorEventId: string },
	iThunkApi
>("eventBid/whatsapp-group-details", async ({ vendorEventId }, thunk) => {
	const response = await apiManager(
		API_ENDPOINTS.GET_WHATS_APP_GROUP_INFO(vendorEventId),
		{},
		API_METHOD_TYPE.GET,
		{ Authorization: getAccessToken() },
		REACT_APP_API_URL,
	);

	if (response && response.data && isApiSuccess(response.status)) {
		return response.data.data?.groupInfo;
	}

	return thunk.rejectWithValue(response?.data);
});

export const getWhatsAppGroupLink = createAsyncThunk<
	{ data: { invitationLink: string } },
	{ vendorEventId: string },
	iThunkApi
>("event/updateEventName", async (payload, thunkAPI) => {
	const accessToken = getAccessToken();
	const resp = await apiManager(
		API_ENDPOINTS.GET_WHATSAPP_GROUP_LINK(payload.vendorEventId),
		{},
		"GET",
		{
			Authorization: accessToken,
		},
		REACT_APP_API_URL,
	);
	if (!resp) return;
	if (!isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return {
		...resp?.data,
	};
});
